import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'
import {
  findResourceOfType,
  globalQueryKeys,
  OpalApiResponseDocument,
} from '@opallabs/api'
import { moments, OpalApiRequestParams } from '@opallabs/api/v3'
import { useWorkspace } from '@opallabs/hooks'
import type { BoardObjectsByBoard, MomentData } from '../types'

type MomentQueryParams = OpalApiRequestParams<'ReadMomentsV3'>['query']
type ResponseDocument = OpalApiResponseDocument<'ReadMomentsV3', 200>
type UseGetMomentOptions<T = ResponseDocument> = {
  momentUuids: string[]
  select?: (data: ResponseDocument) => T
}

const getBoardObjectsByBoard = (
  moment: ResponseDocument['data'][number],
  responseDocument: ResponseDocument
): BoardObjectsByBoard => {
  if (!moment.relationships.board_objects?.data.length) return {}

  return moment.relationships.board_objects.data.reduce<BoardObjectsByBoard>(
    (obj, relationship) => {
      const boardObject = responseDocument.included?.find(
        findResourceOfType('board_object', 'V3', relationship.id)
      )

      const collectionId =
        boardObject?.relationships.board_object_collection !== undefined
          ? (boardObject.relationships.board_object_collection.data?.id ?? null)
          : undefined

      if (boardObject?.relationships.board.data.id) {
        obj[boardObject.relationships.board.data.id] = {
          id: boardObject.id,
          collectionId,
        }
      }

      return obj
    },
    {}
  )
}

const select = (
  responseDocument?: InfiniteData<ResponseDocument>
): MomentData[] => {
  if (!responseDocument) return []
  return responseDocument.pages.reduce<MomentData[]>((allMoments, page) => {
    const moments = page.data.map((moment) => {
      const momentSchedules = moment.relationships.schedules?.data.map(
        (scheduleRelationship) => {
          const schedule = page.included?.find(
            findResourceOfType('moment_schedule', 'V3', scheduleRelationship.id)
          )
          return {
            startDate: schedule!.attributes.start_at,
            endDate: schedule!.attributes.end_at ?? null,
          }
        }
      )

      return {
        id: moment.id,
        isStamp: Boolean(moment.meta?.is_stamp_template),
        paired: Boolean(moment.relationships.paired_block),
        primaryStoryId: moment.relationships.primary_story?.data?.id,
        schedule: momentSchedules?.[0] ?? null,
        scheduleId: moment.relationships.schedules?.data[0]?.id,
        title: moment.attributes.title,
        boardObjectsByBoard: getBoardObjectsByBoard(moment, page),
      }
    })

    return [...allMoments, ...moments]
  }, [])
}

const FILTER_ID_LIMIT = 100

export const useMomentData = <T = InfiniteData<ResponseDocument>>({
  momentUuids,
}: UseGetMomentOptions<T>) => {
  const { uuid: workspaceId } = useWorkspace()
  const getQuery = useCallback(
    (requestFilterOffset: number): MomentQueryParams => ({
      filter: {
        id: [
          ...momentUuids.slice(
            requestFilterOffset,
            requestFilterOffset + FILTER_ID_LIMIT
          ),
        ],
      },
      fields: {
        board_object: ['board', 'board_object_collection'].join(','),
        moment: ['board_objects', 'schedules', 'title', 'primary_story'].join(
          ','
        ),
      },
      include: ['board_objects', 'schedules', 'primary_story'],
    }),
    [momentUuids]
  )

  const { data, isFetchingNextPage, isFetching, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      refetchInterval: false,
      queryKey: globalQueryKeys.momentsByWorkspace(workspaceId, getQuery(0)),
      queryFn: ({ pageParam = 0 }) =>
        moments.readCollection({ query: getQuery(pageParam) }),
      getNextPageParam: (_lastPage, pages) => {
        const resourcesFetched = pages.length * FILTER_ID_LIMIT
        if (resourcesFetched >= momentUuids.length) return undefined
        return resourcesFetched
      },
    })

  const resultsObject = useMemo(() => {
    const selectedData = select(data)
    return {
      fetchingComplete: !hasNextPage && !isFetching,
      data: selectedData,
    }
  }, [data, hasNextPage, isFetching])

  useEffect(() => {
    if (!data) return
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, isFetching, data, fetchNextPage])

  return resultsObject
}
